
import { useState, useRef } from "react";
import API_CONFIG from "../../../Configuration/apiConfig";
import apiClient from "../../../Configuration/apiClient";

export function useStoreDetails(fromDate, toDate, imageHook) {
  const [selectedStore, setSelectedStore] = useState(null);
  const [selectedStoreDetails, setSelectedStoreDetails] = useState(null);
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const [productSummary, setProductSummary] = useState([]);
  const [shelfInfo, setShelfInfo] = useState([]);
  const [selectedMonthData, setSelectedMonthData] = useState(null);
  const [loadingStoreDetails, setLoadingStoreDetails] = useState(false);
  const [error, setError] = useState("");

  const storeCache = useRef({});
  const activeRequestRef = useRef(null);

  const resetDetails = () => {
    storeCache.current = {};
    setSelectedStore(null);
    setSelectedStoreDetails(null);
    setScoreBreakdown(null);
    setProductSummary([]);
    setShelfInfo([]);
    setSelectedMonthData(null);
    imageHook.resetImages();
  };

  const handleStoreClick = async (store, preserveImageIndex = null, forceRefresh = false) => {
    const cacheKey = `${store.storeId}_${fromDate}_${toDate}`;
    activeRequestRef.current = cacheKey;

    if (!forceRefresh && storeCache.current[cacheKey]) {
      const cached = storeCache.current[cacheKey];
      setSelectedStore(store);
      setSelectedStoreDetails(cached.storeData);
      setScoreBreakdown(cached.breakdownData);
      setProductSummary(cached.breakdownData?.availability?.products || []);
      setShelfInfo(cached.shelves);
      setSelectedMonthData(cached.monthData);
      imageHook.setModalActualImages(cached.actualUrls);
      imageHook.setModalAnnotatedImages(cached.annotatedUrls);
      imageHook.setSelectedStoreImages(cached.storeImgUrls);
      const indexToUse = preserveImageIndex ?? 0;
      imageHook.setModalImageIndex(indexToUse);
      imageHook.setActualImageUrl(cached.actualUrls[indexToUse] || "");
      imageHook.setAnnotatedImageUrl(cached.annotatedUrls[indexToUse] || "");
      return;
    }

    setSelectedStore(store);
    setLoadingStoreDetails(true);
    setError("");

    try {
      const month = store.month;
      const year = store.year;
      const monthData = {
        year,
        month,
        label: new Date(year, month - 1).toLocaleString("default", { month: "long" }),
      };
      setSelectedMonthData(monthData);

      const [fromYear, fromMonthNum, fromDay] = fromDate.split("-").map(Number);
      const [toYear, toMonthNum, toDay] = toDate.split("-").map(Number);
      const requestBody = {
        from: { date: fromDay, month: fromMonthNum, year: fromYear },
        to: { date: toDay, month: toMonthNum, year: toYear },
      };

      const [res, breakdownRes] = await Promise.all([
        apiClient.post(
          `${API_CONFIG.ENDPOINTS.ASM_REPORT.ABSTRACTVIEW}?storeId=${store.storeId}`,
          requestBody
        ),
        apiClient.post(
          `${API_CONFIG.BASE_URL}/red-master/${store.storeId}/score-breakdown`,
          requestBody
        ),
      ]);

      const storeData = res.data || null;
      const breakdownData = breakdownRes.data;

      const actualPaths =
        breakdownData?.availability?.imagePathResponse?.s3pathActualFile || [];
      const annotatedPaths =
        breakdownData?.availability?.imagePathResponse?.s3pathAnnotatedFile || [];

      setSelectedStoreDetails(storeData);
      setScoreBreakdown(breakdownData);
      setProductSummary(breakdownData?.availability?.products || []);

      const storeImagePaths = storeData?.imagePaths?.map((p) => `${p}`) || [];

      const shelves = actualPaths.map((_, index) => ({
        shelfNumber: index + 1,
        label: `Shelf ${index + 1}`,
      }));
      setShelfInfo(shelves);

      const indexToRestore = preserveImageIndex ?? 0;

      const storeImgUrls = storeImagePaths.length > 0
        ? await imageHook.generatePresignedUrls(storeImagePaths, monthData.month, monthData.year)
        : [];

      const isStillActive = activeRequestRef.current === cacheKey;
      if (isStillActive) {
        imageHook.setSelectedStoreImages(storeImgUrls);
        setLoadingStoreDetails(false);

        if (preserveImageIndex === null || preserveImageIndex === undefined) {
          imageHook.setCurrentImageIndex(0);
          imageHook.setImageRotation(0);
          imageHook.setModalImageIndex(0);
        }
      }

      storeCache.current[cacheKey] = {
        storeData,
        breakdownData,
        actualUrls: [],
        annotatedUrls: [],
        storeImgUrls,
        shelves,
        monthData,
      };

      // Background: presign the Detail-modal (actual/annotated) images without
      // blocking the Abstract View's spinner; update state/cache when ready.
      Promise.all([
        actualPaths.length > 0
          ? imageHook.generatePresignedUrls(actualPaths, month, year)
          : Promise.resolve([]),
        annotatedPaths.length > 0
          ? imageHook.generatePresignedUrls(annotatedPaths, month, year)
          : Promise.resolve([]),
      ]).then(([actualUrls, annotatedUrls]) => {
        if (storeCache.current[cacheKey]) {
          storeCache.current[cacheKey].actualUrls = actualUrls;
          storeCache.current[cacheKey].annotatedUrls = annotatedUrls;
        }

        // Don't clobber a different store's Detail-modal images if the user
        // has since navigated away from this one.
        if (activeRequestRef.current !== cacheKey) return;

        imageHook.setModalActualImages(actualUrls);
        imageHook.setActualImageUrl(actualUrls[indexToRestore] || "");
        imageHook.setModalAnnotatedImages(annotatedUrls);
        imageHook.setAnnotatedImageUrl(annotatedUrls[indexToRestore] || "");

        actualUrls.slice(0, 3).forEach(src => { new window.Image().src = src; });
        annotatedUrls.slice(0, 3).forEach(src => { new window.Image().src = src; });
      }).catch((err) => {
        console.error("Failed to load detail-view images:", err);
      });
    } catch (err) {
      console.error("Failed to load store details:", err);
      setError("Failed to load store details.");
      setSelectedStoreDetails(null);
      setScoreBreakdown(null);
      setProductSummary([]);
      setShelfInfo([]);
      setSelectedMonthData(null);
      imageHook.resetImages();
      setLoadingStoreDetails(false);
    }
  };

  return {
    selectedStore,
    selectedStoreDetails,
    scoreBreakdown,
    productSummary,
    shelfInfo,
    selectedMonthData,
    loadingStoreDetails,
    error,
    handleStoreClick,
    resetDetails,
  };
}
